import {
  ApiError,
  AssessmentMethodType,
  GET_ASSESSMENTS_ENTRIES_QUERY,
  useUpsertDeleteAssessmentEntryMutation,
} from '@schooly/api';
import {
  CellContext,
  enhanceGridCellHandler,
  GridCellMiddleware,
} from '@schooly/components/data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { convertToAssessmentEntry } from '../../../../../components/uikit-components/AssessmentEntryMethods/utils';
import { AssessmentCellState, UpdateAssessmentEntryParams } from '../types';

export type GradeUpdateParams = {
  cellId: string;
  rowId: string;
  field: string;
  studentId: string;
  methodId: string;
  assessmentId: string;
  selectListOptionId: string | null;
  groupId: string;
  categoryId?: string;
};
type GradeUpdateHandlerProps = {
  cellContext: CellContext<AssessmentCellState>;
  middlewareConfig: GridCellMiddleware<any, any, AssessmentCellState>[];
  updateLocalState: (params: UpdateAssessmentEntryParams & { categoryId: string }) => void;
  onUpdateError?: (error: ApiError) => void;
};

export const useGradeUpdateHandler = ({
  cellContext,
  middlewareConfig,
  updateLocalState,
  onUpdateError,
}: GradeUpdateHandlerProps) => {
  const queryClient = useQueryClient();
  const upsertDeleteAssessmentEntry = useUpsertDeleteAssessmentEntryMutation();

  const updateGrade = async (params: UpdateAssessmentEntryParams & { categoryId?: string }) => {
    const { studentId, methodId, entry, groupId, categoryId = '' } = params;
    const selectListOptionId =
      entry && 'select_list_option_id' in entry ? entry.select_list_option_id : null;

    try {
      const res = await upsertDeleteAssessmentEntry.mutateAsync({
        method_id: methodId,
        recipient_relation_id: studentId,
        data: { group_id: groupId, select_list_option_id: selectListOptionId },
      });

      queryClient.invalidateQueries([GET_ASSESSMENTS_ENTRIES_QUERY]);

      updateLocalState({
        ...params,
        entry: res.assessment_entry ? convertToAssessmentEntry(res.assessment_entry) : null,
        categoryId,
      });
    } catch (err) {
      onUpdateError?.(err as ApiError);
      throw err;
    }
  };

  const enhancedHandler = enhanceGridCellHandler(updateGrade, cellContext, middlewareConfig);

  return ({
    cellId,
    rowId,
    field,
    studentId,
    methodId,
    assessmentId,
    selectListOptionId,
    groupId,
    categoryId,
  }: GradeUpdateParams): Promise<void> => {
    const updateParams: UpdateAssessmentEntryParams & { categoryId?: string } = {
      studentId,
      assessmentId,
      methodId,
      entry:
        selectListOptionId !== null
          ? {
              method: { id: methodId, type: AssessmentMethodType.Grade },
              select_list_option_id: selectListOptionId,
            }
          : null,
      groupId,
      methodType: AssessmentMethodType.Score,
      categoryId,
    };

    return enhancedHandler.withCellId(cellId, rowId, field)(updateParams);
  };
};
